export const COLLAPSE_STORAGE_KEY = 'focusml.assistants.routerCollapsed';

export function readCollapsedIds() {
  try {
    const raw = JSON.parse(window.localStorage.getItem(COLLAPSE_STORAGE_KEY) || '[]');
    if (!Array.isArray(raw)) return new Set();
    return new Set(raw.map(Number).filter((id) => Number.isFinite(id)));
  } catch (err) {
    return new Set();
  }
}

export function writeCollapsedIds(ids) {
  window.localStorage.setItem(COLLAPSE_STORAGE_KEY, JSON.stringify([...ids]));
}

export function specialistCountLabel(count) {
  if (!count) return 'No specialists';
  if (count === 1) return '1 specialist';
  return `${count} specialists`;
}

export function deleteMembershipLine(memberOf) {
  const names = (memberOf || []).map((item) => item.router_name).filter(Boolean);
  if (names.length === 0) return '';
  return `It will also leave ${names.join(', ')}.`;
}

function inTab(assistant, tab) {
  if (tab === 'platform') return true;
  if (tab === 'group') return Boolean(assistant.group_id);
  return !assistant.group_id;
}

function matches(assistant, query) {
  if (!query) return true;
  const hay = [assistant.name, assistant.model, assistant.description, assistant.cloud_provider]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(query);
}

function alsoIn(assistant, exceptRouterId) {
  return (assistant.member_of || [])
    .filter((item) => item.router_id !== exceptRouterId && item.router_name)
    .map((item) => item.router_name);
}

function visibleChildren(router, byId) {
  return (router.team_members || [])
    .map((member) => byId.get(member.assistant_id))
    .filter((child) => child && child.router_mode !== true);
}

function topRow(assistant, extra) {
  return {
    ...assistant,
    tableKey: `assistant-${assistant.id}`,
    rowKind: assistant.router_mode ? 'router' : 'assistant',
    depth: 0,
    parentRouterId: null,
    ...extra,
  };
}

function memberRow(assistant, routerId) {
  return {
    ...assistant,
    tableKey: `member-${routerId}-${assistant.id}`,
    rowKind: 'member',
    depth: 1,
    parentRouterId: routerId,
    specialistCount: 0,
    alsoIn: alsoIn(assistant, routerId),
    expandable: false,
    chevronDisabled: true,
    expanded: false,
  };
}

export function buildAssistantTable({
  assistants,
  tab,
  query,
  collapsedIds,
  page,
  pageSize,
}) {
  const list = assistants || [];
  const byId = new Map(list.map((assistant) => [assistant.id, assistant]));
  const collapsed = collapsedIds || new Set();
  const q = (query || '').trim().toLowerCase();
  const searching = q.length > 0;
  const tabAssistants = list.filter((assistant) => inTab(assistant, tab));
  const nestedIds = new Set();
  tabAssistants
    .filter((assistant) => assistant.router_mode)
    .forEach((router) => {
      visibleChildren(router, byId).forEach((child) => nestedIds.add(child.id));
    });

  const groups = tabAssistants
    .filter((assistant) => !nestedIds.has(assistant.id))
    .map((assistant) => {
      const children = assistant.router_mode ? visibleChildren(assistant, byId) : [];
      const routerMatches = matches(assistant, q);
      const matchingChildren = children.filter((child) => matches(child, q));
      if (searching && !routerMatches && matchingChildren.length === 0) return null;
      const shownChildren = searching
        ? (routerMatches ? children : matchingChildren)
        : (collapsed.has(assistant.id) ? [] : children);
      const expandable = children.length > 0;
      return {
        assistant,
        shownChildren,
        specialistCount: children.length,
        expandable,
        chevronDisabled: !expandable || searching,
        expanded: expandable && (searching || !collapsed.has(assistant.id)),
      };
    })
    .filter(Boolean);

  const size = pageSize > 0 ? pageSize : 10;
  const current = page > 0 ? page : 1;
  const start = (current - 1) * size;
  const pageGroups = groups.slice(start, start + size);
  const rows = [];
  pageGroups.forEach((group) => {
    rows.push(topRow(group.assistant, {
      specialistCount: group.specialistCount,
      alsoIn: alsoIn(group.assistant, null),
      expandable: group.expandable,
      chevronDisabled: group.chevronDisabled,
      expanded: group.expanded,
    }));
    group.shownChildren.forEach((child) => {
      rows.push(memberRow(child, group.assistant.id));
    });
  });
  return { rows, totalTopLevel: groups.length };
}
